import Header from "@/components/Header";
import Hero from "@/components/Hero";
import WhatIsPlasma from "@/components/WhatIsPlasma";
import Benefits from "@/components/Benefits";
import Process from "@/components/Process";
import BonusSection from "@/components/BonusSection";
import Requirements from "@/components/Requirements";
import FAQ from "@/components/FAQ";
import SEOContent from "@/components/SEOContent";
import Footer from "@/components/Footer";
import LazyLoad from "@/components/LazyLoad";
import { Helmet } from "react-helmet-async";

import CityDirectory from "@/components/CityDirectory";

const CentersMap = React.lazy(() => import("@/components/CentersMap"));

import React, { useEffect, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { EDITORIAL_UPDATED_AT } from "@/data/siteMetadata";

const Index = () => {
  const currentYear = new Date().getFullYear();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Small timeout to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>{`Plasmaspende Aufwandsentschädigung & Anmeldebonus ${currentYear}: Bis zu 150€`}</title>
        <meta
          name="description"
          content="Plasma spenden & Geld verdienen: Bis zu 150€ Anmeldebonus für Neuspender plus ca. 30€ Aufwandsentschädigung bar pro Spende bei CSL Plasma & PSE. Jetzt Zentren & Aktionen vergleichen!"
        />
        <meta
          name="keywords"
          content="Plasmaspenden Bonus, Plasmaspende Aufwandsentschädigung, Plasmaspenden Anmeldebonus, CSL Plasma wie viel Geld, CSL Plasma Aktionen, CSL Plasma Bonus 150€, Plasma Service Europe Bonus, Neuspender Bonus Plasmaspende, Plasmaspende Deutschland, Plasma spenden Geld, Willkommensbonus Plasmaspende"
        />
        <link rel="canonical" href="https://plasmaspenden-bonus.de/" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={`Plasmaspende Aufwandsentschädigung & Anmeldebonus ${currentYear}: Bis zu 150€`}
        />
        <meta
          property="og:description"
          content="Plasma spenden & Geld verdienen: Bis zu 150€ Anmeldebonus für Neuspender plus ca. 30€ Aufwandsentschädigung bar pro Spende bei CSL Plasma & PSE."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:url" content="https://plasmaspenden-bonus.de/" />

        {/* Google Site Verification */}
        <meta
          name="google-site-verification"
          content="ALJ1tgtbjqDOUTemce4zniA36zTRJ6GDa3W4XmKZLTU"
        />

        {/* Schema.org Article for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `Plasmaspenden in Deutschland: Bis zu 150€ Anmeldebonus für Neuspender ${currentYear}`,
            description:
              "Bis zu 150€ Anmeldebonus für neue Plasmaspender! Erfahren Sie alles über Ablauf, Voraussetzungen und exklusive Bonusprogramme bei CSL Plasma und Plasma Service Europe.",
            publisher: {
              "@type": "Organization",
              name: "Plasmaspenden Deutschland",
            },
            dateModified: EDITORIAL_UPDATED_AT,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://plasmaspenden-bonus.de",
            },
          })}
        </script>

        {/* Schema.org HowTo for process section */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "Wie funktioniert Plasmaspenden?",
            description:
              "Schritt für Schritt Anleitung zum Plasmaspenden in Deutschland",
            step: [
              {
                "@type": "HowToStep",
                name: "Anmeldung",
                text: "Online oder vor Ort im Plasmazentrum registrieren und ersten Termin vereinbaren.",
              },
              {
                "@type": "HowToStep",
                name: "Gesundheitscheck",
                text: "Kurze ärztliche Untersuchung und medizinischen Fragebogen ausfüllen.",
              },
              {
                "@type": "HowToStep",
                name: "Plasmaspende",
                text: "Die reine Spende dauert je nach Anbieter etwa 30 bis 55 Minuten. Für den Besuch kommt Zeit für Anmeldung, Untersuchung und Erholung hinzu.",
              },
              {
                "@type": "HowToStep",
                name: "Aufwandsentschädigung",
                text: "Nach der Spende erhalten Sie ca. 30 Euro bar ausgezahlt. Als Neuspender winken bis zu 150€ Anmeldebonus!",
              },
            ],
          })}
        </script>

        {/* Schema.org MonetaryAmount for earnings */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Offer",
            name: "Anmeldebonus Plasmaspende Neuspender",
            description:
              "Bis zu 150€ Anmeldebonus für neue Plasmaspender in Deutschland",
            priceSpecification: {
              "@type": "PriceSpecification",
              price: "100-150",
              priceCurrency: "EUR",
              unitText: "Anmeldebonus",
            },
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Startseite",
                item: "https://plasmaspenden-bonus.de/",
              },
            ],
          })}
        </script>

        {/* Schema.org MedicalWebPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Plasmaspenden Deutschland",
            url: "https://plasmaspenden-bonus.de/",
            about: {
              "@type": "MedicalProcedure",
              name: "Plasmaspende",
              procedureType: "https://schema.org/TherapeuticProcedure",
              description:
                "Entnahme von Blutplasma für die Herstellung lebensrettender Medikamente",
            },
            audience: {
              "@type": "PeopleAudience",
              suggestedMinAge: 18,
              suggestedMaxAge: 68,
              audienceType: "Plasmaspender in Deutschland",
            },
          })}
        </script>
      </Helmet>

      <Header />
      <main
        id="main-content"
        className="min-h-screen pt-16"
        itemScope
        itemType="https://schema.org/WebPage"
      >
        <Hero />
        <article itemScope itemType="https://schema.org/Article">
          <WhatIsPlasma />
          <Benefits />
          <Process />
          <BonusSection />
          <Requirements />
          <LazyLoad rootMargin="400px" fallback={<div className="h-[500px]" />}>
            <Suspense fallback={<div className="h-[500px]" />}>
              <CentersMap />
            </Suspense>
          </LazyLoad>
          <CityDirectory />
          <FAQ />
          <SEOContent />
        </article>
        <Footer />
      </main>
    </>
  );
};

export default Index;
